import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SiteSettings {
  maintenance_enabled: boolean;
  title_lv: string;
  title_en: string;
  title_ru: string;
  message_lv: string;
  message_en: string;
  message_ru: string;
}

const BYPASS_KEY = 'haus_preview_bypass';
const ADMIN_SESSION_KEY = 'admin_session';

/** Lovable editor preview + local dev always show the shop. */
function isPreviewEnvironment(): boolean {
  try {
    const host = window.location.hostname;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.includes('-preview--') ||
      host.endsWith('.lovableproject.com') ||
      host.endsWith('.sandbox.lovable.dev')
    );
  } catch { return false; }
}

/** Admins that are logged in (or hold a valid preview link) always see the shop. */
export function hasMaintenanceBypass(): boolean {
  try {
    if (isPreviewEnvironment()) return true;
    if (localStorage.getItem(BYPASS_KEY)) return true;
    if (sessionStorage.getItem(ADMIN_SESSION_KEY)) return true;
  } catch { /* ignore */ }
  return false;
}

export function setMaintenanceBypass(token: string) {
  try {
    localStorage.setItem(BYPASS_KEY, token);
  } catch { /* ignore */ }
}

export function clearMaintenanceBypass() {
  try {
    localStorage.removeItem(BYPASS_KEY);
  } catch { /* ignore */ }
}

export function useMaintenanceMode() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [bypass, setBypass] = useState(hasMaintenanceBypass());

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('maintenance_enabled, title_lv, title_en, title_ru, message_lv, message_en, message_ru')
        .limit(1)
        .maybeSingle();
      if (!cancelled) {
        setSettings((data as SiteSettings) ?? null);
        setLoading(false);
      }
    };

    // Handle preview link: /lv?preview=<token>
    const params = new URLSearchParams(window.location.search);
    const previewToken = params.get('preview');
    if (previewToken) {
      supabase.functions
        .invoke('manage-site-settings', { body: { action: 'verify_bypass', token: previewToken } })
        .then(({ data }) => {
          if (data?.valid) {
            setMaintenanceBypass(previewToken);
            if (!cancelled) setBypass(true);
          }
        })
        .catch(() => { /* ignore */ });
    }

    load();

    const channel = supabase
      .channel(`site-settings-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings' }, () => load())
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    settings,
    loading,
    bypass,
    isMaintenance: !!settings?.maintenance_enabled && !bypass,
  };
}
